/*
 * Keeps personalization service lines in sync with the products they belong to.
 *
 * Every personalized add-to-cart writes the same PERSONALIZATION_LINK_PROPERTY on
 * both lines. After any cart change we realign the service lines with their product:
 * orphans are dropped, quantities are matched, and a service line removed on its own
 * is restored. That way a customer is never charged for a personalization that is no
 * longer attached to anything, nor gets a personalized product for free.
 */
class CartPersonalizationSync {
  constructor() {
    this.running = false;
    this.pending = false;
    this.canAddServiceLines = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'personalization-sync') return;
      return this.reconcile();
    });

    this.reconcile();
  }

  get serviceVariantId() {
    return `${window.personalizationSettings?.variantId || ''}`;
  }

  async reconcile() {
    if (!this.serviceVariantId) return;

    // A reconcile triggered while one is in flight would read a stale cart, so it
    // is collapsed into a single follow-up pass.
    if (this.running) {
      this.pending = true;
      return;
    }
    this.running = true;

    try {
      const cart = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      }).then((response) => response.json());

      const { updates, additions } = this.plan(cart);
      let changed = false;

      if (Object.keys(updates).length) {
        await this.request(routes.cart_update_url, { updates });
        changed = true;
      }

      if (additions.length && this.canAddServiceLines) {
        try {
          await this.request(routes.cart_add_url, { items: additions });
          changed = true;
        } catch (error) {
          // Usually the service variant is unavailable. Retrying on every cart
          // change would just spam the same failure.
          this.canAddServiceLines = false;
          console.error('Could not restore personalization service line', error);
        }
      }

      if (changed) await this.refreshCartUI();
    } catch (error) {
      console.error('Personalization sync failed', error);
    } finally {
      this.running = false;
      if (this.pending) {
        this.pending = false;
        this.reconcile();
      }
    }
  }

  plan(cart) {
    const items = cart.items || [];
    const isServiceLine = (item) => `${item.variant_id}` === this.serviceVariantId;

    const expected = new Map();
    items.forEach((item) => {
      const ref = item.properties?.[PERSONALIZATION_LINK_PROPERTY];
      if (!ref || isServiceLine(item)) return;

      const entry = expected.get(ref) || { quantity: 0, properties: item.properties };
      entry.quantity += item.quantity;
      expected.set(ref, entry);
    });

    const updates = {};
    const covered = new Set();

    items.forEach((item) => {
      if (!isServiceLine(item)) return;

      const ref = item.properties?.[PERSONALIZATION_LINK_PROPERTY];
      // A service line with no link property predates this feature (or was added by
      // hand), so it is left untouched rather than silently removed.
      if (!ref) return;

      covered.add(ref);
      const quantity = expected.get(ref)?.quantity || 0;
      if (quantity !== item.quantity) updates[item.key] = quantity;
    });

    const additions = [];
    expected.forEach((entry, ref) => {
      if (covered.has(ref)) return;
      additions.push({
        id: this.serviceVariantId,
        quantity: entry.quantity,
        properties: this.serviceProperties(ref, entry.properties),
      });
    });

    return { updates, additions };
  }

  serviceProperties(ref, sourceProperties) {
    const propertyName = window.personalizationSettings?.propertyName;
    return {
      ...(propertyName && sourceProperties?.[propertyName]
        ? { [propertyName]: sourceProperties[propertyName] }
        : {}),
      [PERSONALIZATION_LINK_PROPERTY]: ref,
    };
  }

  async request(url, body) {
    const response = await fetch(url, { ...fetchConfig(), body: JSON.stringify(body) });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message);
    return data;
  }

  async refreshCartUI() {
    await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'personalization-sync' });
    await Promise.all([this.refreshCartFooter(), this.refreshCartIcon()]);

    if (window.discoAlpine?.store('cart')) {
      window.discoAlpine.store('cart').fetchItems();
    }
  }

  refreshCartFooter() {
    const footer = document.getElementById('main-cart-footer');
    if (!footer?.dataset?.id) return Promise.resolve();

    return fetch(`${routes.cart_url}?section_id=${footer.dataset.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceElement = html.querySelector('#main-cart-footer .js-contents');
        const targetElement = footer.querySelector('.js-contents');
        if (sourceElement && targetElement) {
          targetElement.innerHTML = sourceElement.innerHTML;
        }
      });
  }

  refreshCartIcon() {
    return fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceElement = html.querySelector('.shopify-section');
        const targetElement = document.getElementById('cart-icon-bubble');
        if (sourceElement && targetElement) {
          targetElement.innerHTML = sourceElement.innerHTML;
        }
      });
  }
}

if (!window.cartPersonalizationSync) {
  window.cartPersonalizationSync = new CartPersonalizationSync();
}
